import { Document } from './Document';
import { transactionTypes } from 'app/configs/transaction';
import { ExternalId } from 'app/utils/externalIds';

export enum ExternalSource {
  DOTLOOP = 'DOTLOOP',
  PAPERLESS_PIPELINE = 'PAPERLESS_PIPELINE',
  SKYSLOPE = 'SKYSLOPE',
  DOCUSIGN_ROOMS = 'DOCUSIGN_ROOMS',
}

// Which system keyed externalSourceID. Christie reuses one id space for two
// folder kinds, so this is what tells them apart when building a deep link.
export enum ExternalSourceIDSetBy {
  BW_PRISM = 'BW_PRISM',
  LOOP_DATA = 'LOOP_DATA',
  CHRISTIE_PROPERTY_FOLDER = 'CHRISTIE_PROPERTY_FOLDER',
  CHRISTIE_DEAL_FOLDER = 'CHRISTIE_DEAL_FOLDER',
}

interface DerivedFieldEntry {
  value: boolean;
  evaluatedAt: string;
}

interface DerivedField {
  values: DerivedFieldEntry[];
  pastValues: DerivedFieldEntry[];
  title?: string;
  description?: string;
  group?: string;
}

interface SourceOfTruth {
  _id: string;
  transactionID: string;
  data: Record<string, any>;
  derivedData?: Record<string, DerivedField>;
  createdAt: string;
  updatedAt: string;
}

export interface Email {
  id: string;
  subject: string;
  body: string;
  recipients: string[];
  sentAt: string;
}

export interface TransactionCreatedBy {
  type?: string;
  userId?: string;
  name?: string;
  source?: string;
}

export interface ComplianceTransaction {
  _id: string;
  name: string;
  type: (typeof transactionTypes)[keyof typeof transactionTypes];
  status?: string;
  documents?: Document[];
  document?: Document;
  agents: Record<string, any>[];
  // Set by GET /transaction/:id only when RBAC is on: canEdit false means the
  // caller reached this transaction through a viewer-level share.
  canEdit?: boolean;
  myAccessLevel?: 'viewer' | 'editor' | null;
  sourceOfTruth?: SourceOfTruth;
  customers?: Record<string, any>[];
  emails?: Email[];
  processingStatus?: any;
  // Deprecated transition-window scalar; read via getPrimaryExternalId(Value).
  externalSourceID?: string;
  // Deprecated transition-window scalar; read via getPrimaryExternalId(Value).
  externalSource?: ExternalSource;
  externalSourceIDSetBy?: string;
  externalIds?: ExternalId[];
  dotloopTransactionNumber?: string;
  representationType?: string;
  dealType?: string;
  propertyType?: string;
  office?: {
    _id: string;
    name: string;
    email?: string;
    contactNo?: string;
  };
  linkedTransaction?: {
    _id: string;
    name: string;
    status?: string;
    milestone?: string;
    type?: string;
    representationType?: string;
    dealType?: string;
    propertyType?: string;
  } | null;
  closingDate?: string;
  milestone?: string;
  address?: any;
  propertyState?: string;
  transactionEmail?: string;
  remarks?: string | null;
  deletedAt?: string;
  createdBy?: TransactionCreatedBy;
  processor?: {
    _id: string;
    name: string;
    email?: string;
  };
  processors?: Array<{
    role: string;
    userId: string;
    name?: string;
    email?: string;
  }>;
  assistantOverrides?: {
    email: string;
    agent: string;
    operation: 'added' | 'removed';
    _id: string;
  }[];
  transactionCoordinatorOverrides?: {
    email: string;
    agent: string;
    operation: 'added' | 'removed';
    _id: string;
  }[];
}
